import { config } from '../appConfig'
import { ARB_CHAIN_ID, BASE_CHAIN_ID, ETH_CHAIN_ID } from '../constants/business'
import { Wallet } from '../providers/WalletProvider'

const BASE_URL = 'https://ledger-scanner.s1.plug-wallet.com'

export const ETH_SUPPORTED_CHAINS_IDS = [ETH_CHAIN_ID, ARB_CHAIN_ID, BASE_CHAIN_ID]

export interface PastBalanceInterval {
  period: number
  unit: 'hour' | 'day' | 'week' | 'month' | 'year'
}

interface BalanceResponse {
  [principalId: string]: string
}

export interface TokenPriceHistory {
  timestamp: number
  price: number
}

export interface NFTToken {
  index: number
  canister: string
  id?: string
  url: string
  standard: string
}

export interface NFTBalanceResponse {
  name: string
  canisterId: string
  standard: string
  tokens: NFTToken[]
  icon: string
  description: string
}

export interface LedgerScannerCustomToken {
  id: string
  address: string
  standard: string
  chain: string
  solana_program_id?: string
}

export interface UserActivityToken {
  chain: string
  address: string
  amount: string
  id: string | null
  amount_usd: number | null
  tx_id: string | null
}

export interface UserActivity {
  tx_id: string
  state: 'confirmed' | 'pending' | 'failed'
  kind: string
  provider: string
  send_token: UserActivityToken
  receive_token: UserActivityToken | null
  link: string | null
  from_addr: string | null
  to_addr: string | null
  ts: number
}

export interface UserActivityFilter {
  receiver?: string
  users?: string[]
  token_id?: string
}

class LedgerScannerClient {
  private static async post(url: string, body: { [key: string]: any }) {
    const result = await fetch(`${BASE_URL}/${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, secret: config.fetchEnv('SCANNER_SECRET') }),
    })

    if (result.ok) {
      if (result.headers.get('Content-Type') === 'application/json') {
        return result.json()
      } else {
        return result.text()
      }
    } else {
      const errorText = (await result.text()) || 'Ledger Scanner fetch error'
      throw new Error(`HTTP ${result.status} error - ${errorText}`)
    }
  }

  public static async fetchUserActivity(
    wallet: Wallet,
    page?: number,
    pageSize?: number,
    filter?: UserActivityFilter
  ): Promise<UserActivity[]> {
    return this.post('activity', {
      wallet: {
        icp: wallet.principalId,
        account_id: wallet.accountId,
        eth: wallet.ethAddress,
        btc: wallet.btcAddress,
        sol: wallet.solAddress,
        doge: wallet.dogeAddress,
      },
      filter,
      page: page || 1,
      page_size: pageSize || 250,
    })
  }

  public static async refreshTransactions(wallet: Wallet) {
    await this.post('refresh-transactions', {
      wallet: {
        icp: wallet.principalId,
        account_id: wallet.accountId,
        eth: wallet.ethAddress,
        btc: wallet.btcAddress,
        sol: wallet.solAddress,
        doge: wallet.dogeAddress,
      },
    })
  }

  public static async registerUser(principal: string, account_id: string): Promise<void> {
    const body = {
      principal,
      account_id,
    }

    await this.post('add-user', body)
  }

  public static async sendToken(
    principal: string,
    account_id: string,
    fcm_token: string
  ): Promise<void> {
    const body = {
      principal,
      account_id,
      fcm_token,
    }

    await this.post('add-fcm', body)
  }

  public static async fetchBalances(
    wallet: Wallet,
    ids: string[],
    customTokens: LedgerScannerCustomToken[],
    chains?: number[]
  ): Promise<BalanceResponse> {
    const body = {
      owner: wallet.principalId,
      btc: wallet.btcAddress,
      sol: wallet.solAddress,
      eth: wallet.ethAddress,
      doge: wallet.dogeAddress,
      secret: config.fetchEnv('SCANNER_SECRET'),
      ids,
      custom: customTokens,
      chains,
    }

    const result: { [key: string]: number } = await this.post('balances/v2', body)
    return Object.fromEntries(
      Object.entries(result).map(([key, value]) => [key, BigInt(value).toString()])
    )
  }

  public static async fetchTokenPriceHistory(id: string) {
    const body = {
      id,
    }
    const response = await this.post('price-history', body)

    return response.prices.map(([timestamp, price]: [number, number]) => ({
      timestamp: Number(timestamp) * 1000,
      price: Number(price),
    }))
  }

  public static async fetchBalanceChange(wallet: Wallet, ids: string[]): Promise<number> {
    const body = {
      owner: wallet.principalId,
      btc: wallet.btcAddress,
      sol: wallet.solAddress,
      eth: wallet.ethAddress,
      doge: wallet.dogeAddress,
      secret: config.fetchEnv('SCANNER_SECRET'),
      ids,
    }

    return this.post('wallet-24h-change', body)
  }

  public static async fetchNFTs(
    principal: string,
    accountId: string
  ): Promise<NFTBalanceResponse[]> {
    const body = {
      principal,
      account_id: accountId,
    }

    return this.post('get-user-nfts', body)
  }

  public static async proxy(
    api: 'stealthex',
    endpoint: string,
    requestOptions?: { POST: any } | { GET: null }
  ) {
    const response = await this.post(api, {
      endpoint,
      request_type: requestOptions || { GET: null },
    })

    return JSON.parse(response.body)
  }
}

export default LedgerScannerClient
